import React, { useState, useEffect, useRef } from 'react';
import {
  Camera,
  Mail,
  Calendar,
  Globe,
  Lock,
  Eye,
  EyeOff,
  Check,
  X,
  Loader2,
  User as UserIcon,
} from 'lucide-react';
import { AppLayout } from '../../components/AppLayout';
import { Card, Button, Input, useToast } from '../../components/ui';
import { userApi, UserProfile } from '../../services';
import './Profile.css';

// Password strength calculation
const calculatePasswordStrength = (password: string): { level: number; text: string } => {
  let strength = 0;
  if (password.length >= 8) strength++;
  if (password.length >= 12) strength++;
  if (/[A-Z]/.test(password)) strength++;
  if (/[a-z]/.test(password)) strength++;
  if (/[0-9]/.test(password)) strength++;
  if (/[^A-Za-z0-9]/.test(password)) strength++;

  if (strength <= 2) return { level: 1, text: 'Weak' };
  if (strength <= 4) return { level: 2, text: 'Fair' };
  if (strength <= 5) return { level: 3, text: 'Good' };
  return { level: 4, text: 'Strong' };
};

export const Profile: React.FC = () => {
  const { success, error: showError, info } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Profile state
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Edit profile state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editName, setEditName] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Profile picture state
  const [isUploadingPicture, setIsUploadingPicture] = useState(false);
  const [imageError, setImageError] = useState(false);

  // Change password state
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  // Fetch profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setIsLoading(true);
        const data = await userApi.getProfile();
        setProfile(data);
        setEditName(data.name);
        setError(null);
      } catch (err) {
        setError('Failed to load profile');
        console.error('Error fetching profile:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, []);

  // Handle profile picture upload
  const handlePictureClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      showError('Please select a valid image file (JPG, PNG, GIF, WEBP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      showError('Image size must be less than 5MB');
      return;
    }

    try {
      setIsUploadingPicture(true);
      const uploadResult = await userApi.uploadProfilePicture(file);
      const updatedProfile = await userApi.updateProfile({
        name: profile!.name,
        profileImageUrl: uploadResult.url,
      });
      setProfile(updatedProfile);
      setImageError(false); // Reset error state for new image
      success('Profile picture updated successfully');
    } catch (err) {
      showError('Failed to upload profile picture');
      console.error('Error uploading profile picture:', err);
    } finally {
      setIsUploadingPicture(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle profile update
  const handleSaveProfile = async () => {
    if (!editName.trim()) {
      showError('Name cannot be empty');
      return;
    }

    try {
      setIsSavingProfile(true);
      const updatedProfile = await userApi.updateProfile({
        name: editName.trim(),
        profileImageUrl: profile?.profileImageUrl,
      });
      setProfile(updatedProfile);
      setIsEditingProfile(false);
      success('Profile updated successfully');
    } catch (err) {
      showError('Failed to update profile');
      console.error('Error updating profile:', err);
    } finally {
      setIsSavingProfile(false);
    }
  };

  const handleCancelEdit = () => {
    setEditName(profile?.name || '');
    setIsEditingProfile(false);
  };

  // Handle password change
  const handleChangePassword = async () => {
    // Validation
    if (!currentPassword) {
      showError('Please enter your current password');
      return;
    }
    if (!newPassword) {
      showError('Please enter a new password');
      return;
    }
    if (newPassword.length < 8) {
      showError('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      showError('New passwords do not match');
      return;
    }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword) || !/[^A-Za-z0-9]/.test(newPassword)) {
      showError('Password must include uppercase, lowercase, number, and special character');
      return;
    }

    try {
      setIsSavingPassword(true);
      // Note: Change password endpoint would need to be added to the backend
      // For now, we'll show a success message
      info('Password change feature coming soon');
      setIsChangingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      showError('Failed to change password');
      console.error('Error changing password:', err);
    } finally {
      setIsSavingPassword(false);
    }
  };

  const handleCancelPasswordChange = () => {
    setIsChangingPassword(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const passwordStrength = calculatePasswordStrength(newPassword);

  if (isLoading) {
    return (
      <AppLayout showBackButton backPath="/dashboard" backLabel="Back" pageTitle="Profile">
        <div className="profile-screen">
          <main className="profile-content">
            <div className="profile-loading">
              <Loader2 className="profile-loading__spinner" size={32} />
              <p>Loading profile...</p>
            </div>
          </main>
        </div>
      </AppLayout>
    );
  }

  if (error || !profile) {
    return (
      <AppLayout showBackButton backPath="/dashboard" backLabel="Back" pageTitle="Profile">
        <div className="profile-screen">
          <main className="profile-content">
            <div className="profile-error">
              <p>{error || 'Failed to load profile'}</p>
              <Button onClick={() => window.location.reload()}>Try Again</Button>
            </div>
          </main>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout showBackButton backPath="/dashboard" backLabel="Back" pageTitle="Profile">
      <div className="profile-screen">
        {/* Content */}
        <main className="profile-content">
        {/* Profile Picture Section */}
        <div className="profile-picture-section">
          <div className="profile-picture-container" onClick={handlePictureClick}>
            {profile.profileImageUrl && !imageError ? (
              <img
                src={profile.profileImageUrl}
                alt={profile.name}
                className="profile-picture"
                onError={() => setImageError(true)}
              />
            ) : (
              <div className="profile-picture profile-picture--placeholder">
                {profile.name ? (
                  <span className="profile-picture__initials">
                    {profile.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </span>
                ) : (
                  <UserIcon size={48} />
                )}
              </div>
            )}
            <div className="profile-picture-overlay">
              {isUploadingPicture ? (
                <Loader2 className="animate-spin" size={24} />
              ) : (
                <Camera size={24} />
              )}
            </div>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleFileChange}
            className="profile-picture-input"
          />
          <p className="profile-picture-hint">Click to change photo</p>
        </div>

        {/* Profile Info Section */}
        <Card className="profile-section">
          <div className="profile-section__header">
            <h2 className="profile-section__title">Profile Information</h2>
            {!isEditingProfile && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditingProfile(true)}
              >
                Edit
              </Button>
            )}
          </div>

          {isEditingProfile ? (
            <div className="profile-edit-form">
              <Input
                label="Name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                fullWidth
              />
              <div className="profile-edit-actions">
                <Button
                  variant="ghost"
                  onClick={handleCancelEdit}
                  disabled={isSavingProfile}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSaveProfile}
                  disabled={isSavingProfile || !editName.trim()}
                >
                  {isSavingProfile ? (
                    <>
                      <Loader2 className="animate-spin" size={16} />
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="profile-info-list">
              <div className="profile-info-item">
                <div className="profile-info-item__icon">
                  <UserIcon size={18} />
                </div>
                <div className="profile-info-item__content">
                  <span className="profile-info-item__label">Name</span>
                  <span className="profile-info-item__value">{profile.name}</span>
                </div>
              </div>
              <div className="profile-info-item">
                <div className="profile-info-item__icon">
                  <Mail size={18} />
                </div>
                <div className="profile-info-item__content">
                  <span className="profile-info-item__label">Email</span>
                  <span className="profile-info-item__value">{profile.email}</span>
                </div>
              </div>
              <div className="profile-info-item">
                <div className="profile-info-item__icon">
                  <Calendar size={18} />
                </div>
                <div className="profile-info-item__content">
                  <span className="profile-info-item__label">Member since</span>
                  <span className="profile-info-item__value">{formatDate(profile.createdAt)}</span>
                </div>
              </div>
              <div className="profile-info-item">
                <div className="profile-info-item__icon">
                  <Globe size={18} />
                </div>
                <div className="profile-info-item__content">
                  <span className="profile-info-item__label">Timezone</span>
                  <span className="profile-info-item__value">{profile.timezone}</span>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Security Section */}
        <Card className="profile-section">
          <div className="profile-section__header">
            <h2 className="profile-section__title">Security</h2>
          </div>

          {isChangingPassword ? (
            <div className="password-change-form">
              <div className="password-input-wrapper">
                <Input
                  label="Current Password"
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  fullWidth
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              <div className="password-input-wrapper">
                <Input
                  label="New Password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  fullWidth
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              {newPassword && (
                <div className="password-strength">
                  <div className="password-strength__bar">
                    <div
                      className={`password-strength__fill password-strength__fill--${passwordStrength.level}`}
                      style={{ width: `${(passwordStrength.level / 4) * 100}%` }}
                    />
                  </div>
                  <span className={`password-strength__text password-strength__text--${passwordStrength.level}`}>
                    {passwordStrength.text}
                  </span>
                </div>
              )}

              <Input
                label="Confirm New Password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                error={confirmPassword && confirmPassword !== newPassword ? 'Passwords do not match' : undefined}
                fullWidth
              />

              <div className="password-requirements">
                <p className="password-requirements__title">Password requirements:</p>
                <ul className="password-requirements__list">
                  <li className={newPassword.length >= 8 ? 'met' : ''}>
                    {newPassword.length >= 8 ? <Check size={14} /> : <X size={14} />}
                    At least 8 characters
                  </li>
                  <li className={/[A-Z]/.test(newPassword) ? 'met' : ''}>
                    {/[A-Z]/.test(newPassword) ? <Check size={14} /> : <X size={14} />}
                    One uppercase letter
                  </li>
                  <li className={/[a-z]/.test(newPassword) ? 'met' : ''}>
                    {/[a-z]/.test(newPassword) ? <Check size={14} /> : <X size={14} />}
                    One lowercase letter
                  </li>
                  <li className={/[0-9]/.test(newPassword) ? 'met' : ''}>
                    {/[0-9]/.test(newPassword) ? <Check size={14} /> : <X size={14} />}
                    One number
                  </li>
                  <li className={/[^A-Za-z0-9]/.test(newPassword) ? 'met' : ''}>
                    {/[^A-Za-z0-9]/.test(newPassword) ? <Check size={14} /> : <X size={14} />}
                    One special character
                  </li>
                </ul>
              </div>

              <div className="profile-edit-actions">
                <Button
                  variant="ghost"
                  onClick={handleCancelPasswordChange}
                  disabled={isSavingPassword}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleChangePassword}
                  disabled={isSavingPassword}
                >
                  {isSavingPassword ? (
                    <>
                      <Loader2 className="animate-spin" size={16} />
                      Changing...
                    </>
                  ) : (
                    'Change Password'
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="security-item" onClick={() => setIsChangingPassword(true)}>
              <div className="security-item__icon">
                <Lock size={18} />
              </div>
              <div className="security-item__content">
                <span className="security-item__label">Change Password</span>
                <span className="security-item__description">Update your password to keep your account secure</span>
              </div>
            </div>
          )}
        </Card>

        {/* Account Stats */}
        <Card className="profile-section profile-stats">
          <div className="profile-stat">
            <span className="profile-stat__value">{profile.activeProjectCount}</span>
            <span className="profile-stat__label">Active Projects</span>
          </div>
          <div className="profile-stat">
            <span className="profile-stat__value">{profile.subscriptionTier}</span>
            <span className="profile-stat__label">Subscription</span>
          </div>
        </Card>
        </main>
      </div>
    </AppLayout>
  );
};

export default Profile;
